import { mergeData } from 'vue-functional-data-merge';
import pluckProps from '@/utils/pluck-props';
import { concat } from '@/utils/array';
import { definePropertyOrAssign } from '@/utils/object';
import { addClass, removeClass } from '@/utils/dom';
import Link, { propsFactory as linkPropsFactory } from '@/components/shared/modal/modal-link';

const btnProps = {
    block: {
        type: Boolean,
        default: false,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    size: {
        type: String,
        default: null,
    },
    variant: {
        type: String,
        default: null,
    },
    type: {
        type: String,
        default: 'button',
    },
    pressed: {
        // tri-state prop: true, false or null
        // => on, off, not a toggle
        type: Boolean,
        default: null,
    },
};

const linkProps = linkPropsFactory();
delete linkProps.href.default;
delete linkProps.to.default;
const linkPropKeys = Object.keys(linkProps);

export const props = Object.assign(linkProps, btnProps);

function handleFocus(evt) {
    if (evt.type === 'focusin') {
        addClass(evt.target, 'focus');
    } else if (evt.type === 'focusout') {
        removeClass(evt.target, 'focus');
    }
}

export default {
    functional: true,
    props,
    render(h, ref) {
        let ref2;

        const compProps = ref.props;
        const data = ref.data;
        const listeners = ref.listeners;
        const children = ref.children;

        const isLink = Boolean(compProps.href || compProps.to);
        const isToggle = typeof compProps.pressed === 'boolean';
        const on = {
            click: function click(e) {
                if (compProps.disabled && e instanceof Event) {
                    e.stopPropagation();
                    e.preventDefault();
                } else if (isToggle) {
                    // Concat will normalize the value to an array
                    // without double wrapping an array value in an array.
                    concat(listeners['update:pressed']).forEach((fn) => {
                        if (typeof fn === 'function') {
                            fn(!compProps.pressed);
                        }
                    });
                }
            },
        };

        if (isToggle) {
            on.focusin = handleFocus;
            on.focusout = handleFocus;
        }

        const dataTabIndex = data.attrs ? data.attrs.tabindex : null;
        const tabIndex = compProps.disabled && isLink ? '-1' : dataTabIndex;

        const componentData = {
            staticClass: 'btn',
            class: [
                compProps.variant
                    ? `btn-${ compProps.variant }`
                    : 'btn-secondary',
                ((ref2 = {}),
                definePropertyOrAssign(
                    ref2,
                    `btn-${ compProps.size }`,
                    Boolean(compProps.size),
                ),
                definePropertyOrAssign(ref2, 'btn-block', compProps.block),
                definePropertyOrAssign(ref2, 'disabled', compProps.disabled),
                definePropertyOrAssign(ref2, 'active', compProps.pressed),
                ref2),
            ],
            compProps: isLink ? pluckProps(linkPropKeys, compProps) : null,
            attrs: {
                type: isLink ? null : compProps.type,
                disabled: isLink ? null : compProps.disabled,
                // Data attribute not used for js logic,
                // but only for BS4 style selectors.
                'data-toggle': isToggle ? 'button' : null,
                'aria-pressed': isToggle ? String(compProps.pressed) : null,
                // Tab index is used when the component becomes a link.
                // Links are tabable, but don't allow disabled,
                // so we mimic that functionality by disabling tabbing.
                tabindex: tabIndex,
            },
            on,
        };

        return h(
            isLink ? Link : 'button',
            mergeData(data, componentData),
            children,
        );
    },
};
